import type { ReactNode } from 'react'
import { useLocation, useMatch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  SETTINGS_BODY,
  SETTINGS_HEADER,
  SETTINGS_STACK,
  SKELETON_TILE,
  SettingsSectionSkeleton,
} from '../settings/settings-section'
import {
  ROW_CHEVRON_SKELETON,
  ROW_ICON_SKELETON,
  SETTINGS_CONTENT_CLASS,
  SETTINGS_READING_CLASS,
  SETTINGS_SIDEBAR_CLASS,
  SettingsTitleSkeleton,
  SidebarRowSkeleton,
} from '../settings/account-pending'
import { SectionHeading } from '../shared/section-heading'
import { Skeleton as TextSkeleton, TextBars } from '../shared/skeleton'
import { COMPONENT_DEMOS, ADMIN_PAGES, ADMIN_WIDE_PAGES, adminPageOf, type AdminPageKey } from './admin-pages'
import { LogsPending } from './wide-event-logs-pending'
import { MetricsPending } from './metrics-pending'
import { SessionsSectionPending } from './sessions-pending'

// The dev playground's pending UIs, drawn from the markup they stand in for (admin.tsx on SettingsShell, the
// tree in admin-nav.tsx, the pages) so each lands where its skeleton was. On the eager path: no icons, no
// tree, no gallery; the copy the pages share with them lives here.

/** The email page's note over the gallery, in SandboxResetHint's box; as a placeholder, its words as TextBars. */
export function EmailsNote(props: { pending?: boolean; icon?: ReactNode }) {
  const text = (
    <>
      Each preview is the exact output that gets sent, snapshotted in{' '}
      <code className="text-xs">catalog.unit.test.ts</code>: a unit test fails when a template changes unexpectedly, and{' '}
      <code className="text-xs">bun run test -u</code> accepts the change.
    </>
  )
  return (
    <div className="bg-muted/50 text-muted-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
      {props.pending ? <Skeleton className="size-4 shrink-0 rounded-sm" /> : props.icon}
      <span>{props.pending ? <TextBars>{text}</TextBars> : text}</span>
    </div>
  )
}

export const ERRORS_DESCRIPTION = (
  <>
    Server 500s and uncaught client errors, deduped by fingerprint, with their occurrences from the wide events. The
    first production occurrence of a new error auto-opens a GitHub issue (picked up by the{' '}
    <code className="text-xs">claude-issue-fix</code> workflow). Resolve, ignore or reopen each; a resolved issue that
    recurs reopens on its own.
  </>
)

export const TRACES_DESCRIPTION = (
  <>
    Distributed traces from the wide events in ClickHouse: one row per page load or API call, and its spans on one time
    axis, from the browser&rsquo;s fetch to the server&rsquo;s logs.
  </>
)

type CachedTemplate = { id: string; name: string }

/** The templates the layout's loader asked for, read without waiting (a pending UI never suspends). */
function useCachedTemplates(): CachedTemplate[] | undefined {
  const queryKey = useMatch({
    from: '/_app/admin',
    shouldThrow: false,
    select: (match) => match.context.emailTemplatesQueryOptions?.queryKey,
  })
  return useQueryClient().getQueryData<CachedTemplate[]>(queryKey ?? ['_'])
}

/** The whole playground while its layout loads: the title bar's trail, the tree, and the page it opens. */
export function AdminPending() {
  const page = useLocation({ select: (location) => adminPageOf(location.pathname) })
  const label = ADMIN_PAGES.find((candidate) => candidate.key === page)?.label ?? ''
  // SettingsShell's `fill`, which only the playground sets.
  const fill = page === 'transitions'
  return (
    <div className="flex h-full flex-col">
      <SettingsTitleSkeleton crumbs={['Admin', label]} />
      <div
        className={cn(
          'scrollbar relative min-h-0 flex-1 md:overflow-x-clip md:overflow-y-visible',
          fill ? 'flex flex-col max-md:overflow-hidden' : 'max-md:overflow-y-scroll',
        )}
      >
        <div className={cn('flex', fill ? 'min-h-0 flex-1' : 'min-h-full')}>
          <div className={SETTINGS_SIDEBAR_CLASS}>
            <div className="h-full overflow-hidden px-3 py-4 sm:px-5">
              <AdminTreeSkeleton page={page} />
            </div>
          </div>
          <div className={cn(SETTINGS_CONTENT_CLASS, fill && 'flex flex-col')}>
            {/* A wide page takes the whole width here, as it does loaded. */}
            <div
              className={cn(
                'w-full',
                fill && 'flex min-h-0 flex-1 flex-col',
                !ADMIN_WIDE_PAGES.includes(page) && SETTINGS_READING_CLASS,
              )}
            >
              <AdminPagePending page={page} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SECTIONS_CLASS = 'mt-0.5 ml-4 flex flex-col gap-0.5 pl-px'
const SECTION_ROW = 'flex min-w-0 items-baseline gap-2 rounded-md px-2 py-1.5 text-sm'

/** The tree (admin-nav.tsx) as it will open here: the page's row, and its sections unfolded under it. */
function AdminTreeSkeleton(props: { page: AdminPageKey }) {
  const templates = useCachedTemplates()
  const sections = {
    emails: templates?.map((template) => template.name) ?? [],
    components: COMPONENT_DEMOS.map((demo) => demo.label),
  }
  return (
    <div className="flex flex-col gap-0.5">
      {ADMIN_PAGES.map((page) => {
        const branch = page.key === 'emails' || page.key === 'components'
        const here = page.key === props.page
        return (
          <div key={page.key} className="flex flex-col">
            <SidebarRowSkeleton
              lead={ROW_ICON_SKELETON}
              label={<TextBars>{page.label}</TextBars>}
              trail={branch ? ROW_CHEVRON_SKELETON : undefined}
              here={here && !branch}
              className={cn(here && branch && 'font-medium')}
            />
            {branch && here && (
              <ol className={SECTIONS_CLASS}>
                {(page.key === 'emails' ? sections.emails : sections.components).map((label) => (
                  <li key={label} className={SECTION_ROW}>
                    <span className="min-w-0">
                      <TextBars>{label}</TextBars>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** A page's own pending, inside the loaded layout (each route's `pendingComponent`) or the layout's. */
export function AdminPagePending(props: { page: AdminPageKey }) {
  if (props.page === 'emails') return <EmailsPending />
  if (props.page === 'errors') return <ErrorsPending />
  if (props.page === 'logs') return <LogsPending />
  if (props.page === 'traces') return <TracesPending />
  if (props.page === 'sessions') return <SessionsSectionPending />
  if (props.page === 'metrics') return <MetricsPending />
  if (props.page === 'components') return <ComponentsPending />
  return <TransitionsPending />
}

export function EmailsPending() {
  return (
    <div className={SETTINGS_STACK}>
      <EmailsNote pending />
      <EmailGallerySkeleton />
    </div>
  )
}

export function EmailGallerySkeleton() {
  return (
    <>
      <EmailControlsSkeleton />
      {Array.from({ length: 2 }, (_, i) => (
        <EmailCardSkeleton key={i} />
      ))}
    </>
  )
}

/** The pinned controls: three labelled `sm` selects. */
export function EmailControlsSkeleton() {
  return (
    <div className="-my-3 flex flex-wrap gap-x-4 gap-y-2 py-3">
      {[64, 88, 96].map((width) => (
        <div key={width} className="flex items-center gap-2">
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-7 rounded-md" style={{ width }} />
        </div>
      ))}
    </div>
  )
}

/** A template's card: its heading and actions, the three facts, the subject line and the rendered email. */
export function EmailCardSkeleton() {
  return (
    <div className="bg-card overflow-hidden rounded-2xl border">
      <div className={cn(SETTINGS_HEADER, 'flex flex-col gap-4')}>
        <SectionHeading
          icon={null}
          tone={SKELETON_TILE}
          title={<TextSkeleton chars={20} />}
          description={<TextSkeleton chars={16} />}
          actions={
            <>
              <Skeleton className="h-7 w-20 rounded-lg" />
              <Skeleton className="h-7 w-24 rounded-lg" />
            </>
          }
        />
        <div className="flex flex-col gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-3/4" />
            </div>
          ))}
        </div>
      </div>
      <div className={cn(SETTINGS_BODY, 'flex flex-col gap-2')}>
        <Skeleton className="h-5 w-64 max-w-full" />
        <Skeleton className="mt-2 h-[600px] w-full rounded-lg" />
      </div>
    </div>
  )
}

export function ErrorsPending() {
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSectionSkeleton title="Errors" description={ERRORS_DESCRIPTION}>
        <ErrorsPanelSkeleton />
      </SettingsSectionSkeleton>
    </div>
  )
}

/** The errors panel while its rows load: the status, range and order controls with search, the four counts, the issues. */
export function ErrorsPanelSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-9 w-[220px] rounded-lg" />
        <Skeleton className="h-9 w-[132px] rounded-lg" />
        <Skeleton className="h-7 w-28 rounded-md" />
        <Skeleton className="h-9 w-64 max-w-full rounded-lg" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[77px] rounded-lg" />
        ))}
      </div>
      <div className="flex flex-col gap-px overflow-hidden rounded-xl border">
        <Skeleton className="h-8 rounded-none" />
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-[76px] rounded-none" />
        ))}
      </div>
    </div>
  )
}

export function TracesPending() {
  return (
    <div className={SETTINGS_STACK}>
      <SettingsSectionSkeleton title="Traces" description={TRACES_DESCRIPTION}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-8 w-28 rounded-lg" />
            <Skeleton className="h-8 w-32 rounded-lg" />
            <Skeleton className="h-8 w-32 rounded-lg" />
            <Skeleton className="h-9 w-56 rounded-lg" />
          </div>
          <TracesListSkeleton />
        </div>
      </SettingsSectionSkeleton>
    </div>
  )
}

/** The explorer's list beside the picked trace's pane. */
export function TracesListSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border lg:flex-row">
      <div className="flex shrink-0 flex-col border-b lg:w-80 lg:border-r lg:border-b-0 xl:w-96">
        <div className="flex flex-col gap-1 border-b px-3 py-2">
          <Skeleton className="h-10 w-full" />
          <span className="text-muted-foreground flex gap-3 text-[11px]">
            <span>p50</span>
            <span>p95</span>
            <span>max</span>
          </span>
        </div>
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex flex-col gap-1.5 border-b px-3 py-2 last:border-b-0">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="ml-3.5 h-3 w-1/2" />
          </div>
        ))}
      </div>
      <div className="min-w-0 flex-1">
        <TraceDetailSkeleton />
      </div>
    </div>
  )
}

/**
 * A trace's waterfall: the facts strip, the span search, the operation / service / time header and its span rows;
 * with `header`, the trace page's title block.
 */
export function TraceDetailSkeleton(props: { header?: boolean }) {
  const waterfall = (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Skeleton className="h-5 w-20 rounded-4xl" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="ml-auto h-4 w-12" />
      </div>
      <div className="flex items-center gap-3 border-b px-2 py-1.5">
        <Skeleton className="h-7 w-52 rounded-lg" />
        <Skeleton className="h-3 w-32" />
      </div>
      <div className="text-muted-foreground flex gap-2 border-b px-2 pt-1.5 pb-0.5 text-[10px] font-semibold tracking-wide uppercase">
        <span className="w-[40%]">Operation</span>
        <span>Service</span>
      </div>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-2 border-b px-2 py-2 last:border-b-0">
          <Skeleton className="h-4 w-[38%]" />
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 flex-1" />
        </div>
      ))}
    </div>
  )
  if (!props.header) return waterfall
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <span className="text-2xl font-semibold tracking-tight">Trace</span>
        <Skeleton className="h-4 w-72 max-w-full" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="overflow-hidden rounded-xl border">{waterfall}</div>
    </div>
  )
}

export function TraceDetailPending() {
  return (
    <div className={SETTINGS_STACK}>
      <span className="text-primary text-sm">← Traces</span>
      <TraceDetailSkeleton header />
    </div>
  )
}

// Each demo's inner boxes at their measured heights, a phone's (wrapping) then the width beside the tree.
const DEMO_BOXES: Record<(typeof COMPONENT_DEMOS)[number]['id'], string[]> = {
  'demo-avatars': ['h-[239px] md:h-[157px]', 'h-[290px] md:h-[133px]'],
  'demo-toasts': [
    'h-[146px] md:h-[102px]',
    'h-[146px] md:h-[102px]',
    'h-[102px]',
    'h-[146px] md:h-[102px]',
    'h-[102px]',
  ],
  'demo-fetching': ['h-[234px] md:h-[180px]', 'h-[412px] md:h-[377px]'],
}

export function ComponentsPending() {
  return (
    <div className={SETTINGS_STACK}>
      {COMPONENT_DEMOS.map((demo) => (
        <SettingsSectionSkeleton key={demo.id} title={demo.label} description={demo.description}>
          <div className="flex flex-col gap-6">
            {/* The fetching demo's bar sits first, a pixel tall, and takes its gap. */}
            {demo.id === 'demo-fetching' && <div className="border-t border-transparent" />}
            {DEMO_BOXES[demo.id].map((height, i) => (
              // oxlint-disable-next-line react/no-array-index-key -- a fixed placeholder list
              <div key={i} className={cn('flex flex-col gap-4 rounded-lg border p-4', height)}>
                <Skeleton className="h-4 w-28" />
              </div>
            ))}
          </div>
        </SettingsSectionSkeleton>
      ))}
    </div>
  )
}

/** The transitions playground: its Jobs / Teams strip over the pane. */
export function TransitionsPending() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 md:min-h-[70vh]">
      <div className="flex gap-2 border-b pb-3">
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-6 w-20" />
      </div>
      <Skeleton className="min-h-0 flex-1 rounded-xl" />
    </div>
  )
}
